import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";

import { Route } from "src/shared/constants/route";

import { decodeBadgeSummary } from "./summary";

export default function SyncSummary({ value }: { value?: string | null }) {
  const summary = useMemo(() => decodeBadgeSummary(value), [value]);
  if (!summary)
    return (
      <p>
        Your game scores and progress will appear here after your first sync.
      </p>
    );
  if ("error" in summary) return <p role="alert">{summary.error}</p>;
  const recorded = summary.games.filter((game) => game.metrics.length);
  const unrecorded = summary.games.filter((game) => !game.metrics.length);
  return (
    <Summary aria-label="Saved badge activity">
      <div>
        <h2>Your synced badge</h2>
        <p>
          From your last saved sync. Play, explore, then sync again to update
          these records.
        </p>
      </div>
      {summary.partial && (
        <p role="status">
          This backup is partial. Only complete records are shown; some progress
          may be missing.
        </p>
      )}
      <Overview>
        <div>
          <Ring $percent={summary.percent}>
            <strong>{summary.percent}%</strong>
          </Ring>
          <h3>Badge progress</h3>
        </div>
        <Bars>
          {summary.progress.map(({ label, count, total }) => (
            <div key={label}>
              <ProgressLabel>
                <span>{label}</span>
                <strong>
                  {count} / {total}
                </strong>
              </ProgressLabel>
              <progress
                aria-label={label}
                value={Math.min(count, total)}
                max={total}
              />
            </div>
          ))}
          <p>
            {summary.contacts}{" "}
            {summary.contacts === 1 ? "connection" : "connections"} saved ·{" "}
            <Link to={Route.BADGE_CONNECT}>View connections</Link>
          </p>
        </Bars>
      </Overview>
      <p className="caption">
        Progress combines app discovery, sponsor visits and the Solana scavenger
        hunt using the badge’s event totals. Goose Games awards are tracked
        separately.
      </p>
      <div>
        <h2>Game records</h2>
        <p>
          {recorded.length} {recorded.length === 1 ? "game has" : "games have"}{" "}
          saved stats. Best times are lower-is-better.
        </p>
      </div>
      {recorded.length > 0 && (
        <Games>
          {recorded.map((game) => (
            <Game key={game.name}>
              <h3>{game.name}</h3>
              <dl>
                {game.metrics.map(({ label, value, unit }) => (
                  <div key={label}>
                    <dt>{label}</dt>
                    <dd>
                      {value.toLocaleString()}
                      {unit && <small> {unit}</small>}
                    </dd>
                  </div>
                ))}
              </dl>
            </Game>
          ))}
        </Games>
      )}
      <details open={!recorded.length}>
        <summary>Not recorded yet ({unrecorded.length})</summary>
        <p>{unrecorded.map((game) => game.name).join(" · ")}</p>
        <p className="caption">
          Finish a game to save a record on your badge, then sync. Games that
          don’t save stats won’t have a score here.
        </p>
      </details>
    </Summary>
  );
}

const Summary = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 24px;
  padding-top: 28px;
  border-top: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  h2,
  h3 {
    margin: 0 0 8px;
    color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  }
  h2 {
    font-size: 24px;
  }
  h3 {
    font-size: 18px;
  }
  .caption,
  dt {
    color: ${({ theme }) => theme.globalConstants.color.textSecondary};
    font-size: 14px;
  }
  summary {
    cursor: pointer;
    font-weight: 700;
    margin-bottom: 12px;
  }
  details p + p {
    margin-top: 12px;
  }
`;
const Overview = styled.div`
  display: grid;
  grid-template-columns: 160px minmax(0, 1fr);
  align-items: center;
  gap: 28px;
  padding: 24px;
  border-radius: 16px;
  background: ${({ theme }) =>
    theme.globalConstants.color.backgroundPrimaryLight};
  > div:first-child {
    text-align: center;
  }
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;
const Ring = styled.div<{ $percent: number }>`
  width: 116px;
  height: 116px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  margin: 0 auto 12px;
  background: conic-gradient(
    #0e3460 ${({ $percent }) => $percent * 3.6}deg,
    #ffffff 0
  );
  strong {
    display: grid;
    place-items: center;
    width: 92px;
    height: 92px;
    border-radius: 50%;
    background: #ceeaf6;
    color: #0e3460;
    font-size: 30px;
  }
`;
const Bars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  progress {
    display: block;
    width: 100%;
    height: 8px;
    border: 0;
    border-radius: 8px;
    overflow: hidden;
    appearance: none;
    background: #ffffff;
  }
  progress::-webkit-progress-bar {
    background: #ffffff;
  }
  progress::-webkit-progress-value {
    background: #0e3460;
    border-radius: 8px;
  }
  progress::-moz-progress-bar {
    background: #0e3460;
  }
`;
const ProgressLabel = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
  strong {
    white-space: nowrap;
  }
`;
const Games = styled.div`
  display: grid;
  align-items: start;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`;
const Game = styled.article`
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 12px;
  padding: 20px;
  dl {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
  }
  dd {
    margin: 2px 0 0;
    font-size: 28px;
    font-weight: 700;
    color: ${({ theme }) => theme.globalConstants.color.brandSecondary};
  }
  small {
    font-size: 14px;
    font-weight: 400;
  }
`;
